import { useEffect, useState, useMemo } from 'react';
import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { api } from '../lib/api';
import type { ListMeta } from '../types/api';

/**
 * Paginated, filterable list over any /api/v1 collection. Every list page in the app
 * is this hook plus a column definition — which is how thirty screens stay consistent.
 */
export function useList<T>(path: string, initialFilters: Record<string, unknown> = {}, options: { perPage?: number; enabled?: boolean; /** Filters from an active stat tile (useStatFilter), merged over the others. */ extra?: Record<string, unknown>; /** Server sort to start with, e.g. 'priority' or '-reportedAt'. */ initialSort?: string } = {}) {
  const [page, setPage] = useState(1);
  const [filters, setFiltersState] = useState<Record<string, unknown>>(initialFilters);
  // A deep link (?search=…, e.g. from My Day) opens the list already filtered.
  const [search, setSearch] = useState(() => { try { return new URLSearchParams(window.location.search).get('search') ?? ''; } catch { return ''; } });
  const [sort, setSort] = useState<string | undefined>(options.initialSort);

  const extraKey = JSON.stringify(options.extra ?? {});
  // A tile switched on or off starts the list again from page 1.
  useEffect(() => { setPage(1); }, [extraKey]);
  const query = useMemo(() => ({ ...filters, ...(JSON.parse(extraKey) as Record<string, unknown>), page, perPage: options.perPage ?? 25, search: search || undefined, sort }), [filters, extraKey, page, options.perPage, search, sort]);

  const result = useQuery({
    queryKey: [path, query],
    queryFn: async () => api.get<T[]>(path, query),
    placeholderData: keepPreviousData,
    enabled: options.enabled ?? true,
  });

  const setFilters = (next: Record<string, unknown>) => { setPage(1); setFiltersState(next); };
  const setFilter = (key: string, value: unknown) => setFilters({ ...filters, [key]: value === '' ? undefined : value });

  return {
    rows: (result.data?.data ?? []) as T[],
    meta: result.data?.meta as ListMeta | undefined,
    /** What the body carries next to data and meta (GET /lettings/viewings' `counts` and `window`, say). */
    extra: result.data?.extra,
    isLoading: result.isLoading,
    isFetching: result.isFetching,
    error: result.error as Error | null,
    refetch: result.refetch,
    /** Everything the list is asked with now (filters, tile, search, sort) — what "Export CSV" sends. */
    query,
    page, setPage, filters, setFilters, setFilter, search, setSearch: (s: string) => { setPage(1); setSearch(s); }, sort, setSort,
  };
}
